import { type FC, useEffect, useId, useRef, useState } from 'react';

import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import type { VideoThumbnailCandidate } from '@/types';

import { tr } from './translations';

const CUSTOM_THUMBNAIL_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

// Inline, not `aspect-video`: the host Tailwind themes do not generate that utility.
const TILE_ASPECT = { aspectRatio: '16 / 9' };

// The padding leaves a transparent gap between the ring and the image inside.
const TILE_CLASS = 'rounded-lg p-0.5 outline-none ring-2 transition-shadow focus-visible:ring-white';

interface VideoThumbnailPickerProps {
  candidates: VideoThumbnailCandidate[];
  selectedIndex: number | null;
  // Each resolves to whether the change was saved. The panel closes at once, since
  // the host shows the new poster optimistically, and reopens with an error on failure.
  onSelect: (index: number) => Promise<boolean>;
  onUpload?: (file: File) => Promise<boolean>;
  onOpen?: () => void;
  translations?: Record<string, string>;
}

// Sits top-left because the remove control keeps the top-right corner.
export const VideoThumbnailPicker: FC<VideoThumbnailPickerProps> = ({
  candidates,
  selectedIndex,
  onSelect,
  onUpload,
  onOpen,
  translations,
}) => {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headingId = useId();

  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open]);

  const openPanel = () => {
    setFailed(false);
    setOpen(true);
    onOpen?.();
  };

  const closePanel = () => setOpen(false);

  const save = async (change: () => Promise<boolean>) => {
    setFailed(false);
    setOpen(false);
    const saved = await change();
    if (!saved) {
      setFailed(true);
      setOpen(true);
    }
  };

  const frameLabel = tr(translations, 'video.thumbnail_frame');

  return (
    <>
      <button
        type="button"
        onClick={openPanel}
        aria-haspopup="dialog"
        className="absolute left-2 top-2 z-10 inline-flex h-6 items-center gap-1 rounded-full bg-black/70 pl-2 pr-2.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-white hover:text-black"
      >
        <PhotoIcon className="size-3.5" aria-hidden="true" />
        {tr(translations, 'video.choose_thumbnail')}
      </button>

      {open && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          tabIndex={-1}
          className="absolute inset-0 z-20 flex flex-col gap-3 overflow-y-auto rounded-xl bg-black/70 p-4 outline-none backdrop-blur-sm"
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return;
            // Headless UI dialogs that host the composer skip Escape once it is default-prevented.
            event.preventDefault();
            closePanel();
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <h3 id={headingId} className="text-sm font-medium text-white">
              {tr(translations, 'video.thumbnail_dialog')}
            </h3>
            <button
              type="button"
              onClick={closePanel}
              aria-label={tr(translations, 'video.close')}
              className="-m-1.5 flex items-center rounded-full p-1.5 transition-colors hover:bg-white/10"
            >
              <XMarkIcon className="size-5 text-white/70" aria-hidden="true" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {candidates.map((candidate) => {
              const selected = candidate.index === selectedIndex;

              return (
                <button
                  key={candidate.index}
                  type="button"
                  aria-label={`${frameLabel} ${candidate.index + 1}`}
                  aria-pressed={selected}
                  onClick={() => void save(() => onSelect(candidate.index))}
                  style={TILE_ASPECT}
                  className={cn(TILE_CLASS, selected ? 'ring-white' : 'ring-transparent hover:ring-white/80')}
                >
                  <img
                    src={candidate.previewUrl}
                    alt=""
                    className="block size-full rounded-md bg-white/10 object-cover"
                  />
                </button>
              );
            })}

            {onUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={CUSTOM_THUMBNAIL_ACCEPT}
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void save(() => onUpload(file));
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={TILE_ASPECT}
                  className={cn(TILE_CLASS, 'group ring-transparent hover:ring-white/80')}
                >
                  <span className="flex size-full flex-col items-center justify-center gap-1.5 rounded-md bg-white/10 px-2 text-center text-white/80 transition-colors group-hover:bg-white/20 group-hover:text-white">
                    <PhotoIcon className="size-6" aria-hidden="true" />
                    <span className="text-xs font-medium">{tr(translations, 'video.upload_thumbnail')}</span>
                  </span>
                </button>
              </>
            )}
          </div>

          {failed && (
            <p role="alert" className="text-xs text-red-300">
              {tr(translations, 'video.thumbnail_error')}
            </p>
          )}
        </div>
      )}
    </>
  );
};
